/**
 * ============================================================
 * KEJJA OS — src/hooks/useLibrary.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  libraryApi,
  type FolderCreatePayload, type FolderUpdatePayload,
  type ItemCreatePayload, type ItemUpdatePayload, type ItemListParams,
} from '@/lib/libraryApi';

const invalidate = (qc: ReturnType<typeof useQueryClient>, homeId: string) => {
  qc.invalidateQueries({ queryKey: ['library', homeId] });
};

// Pulse
export function useLibraryPulse(homeId: string | undefined) {
  return useQuery({
    queryKey: ['library', homeId, 'pulse'],
    queryFn: () => libraryApi.pulse(homeId!),
    enabled: Boolean(homeId),
    staleTime: 30 * 1000,
  });
}

// Folders
export function useFolders(homeId: string | undefined, scope?: 'all' | 'shared' | 'personal') {
  return useQuery({
    queryKey: ['library', homeId, 'folders', scope],
    queryFn: () => libraryApi.listFolders(homeId!, scope),
    enabled: Boolean(homeId),
  });
}

export function useCreateFolder(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FolderCreatePayload) => libraryApi.createFolder(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateFolder(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, patch }: { folderId: string; patch: FolderUpdatePayload }) =>
      libraryApi.updateFolder(homeId!, folderId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteFolder(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folderId: string) => libraryApi.deleteFolder(homeId!, folderId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// Items
export function useItems(homeId: string | undefined, params?: ItemListParams) {
  return useQuery({
    queryKey: ['library', homeId, 'items', params],
    queryFn: () => libraryApi.listItems(homeId!, params),
    enabled: Boolean(homeId),
  });
}

export function useItem(homeId: string | undefined, itemId: string | undefined) {
  return useQuery({
    queryKey: ['library', homeId, 'item', itemId],
    queryFn: () => libraryApi.getItem(homeId!, itemId!),
    enabled: Boolean(homeId) && Boolean(itemId),
  });
}

export function useCreateItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ItemCreatePayload) => libraryApi.createItem(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: ItemUpdatePayload }) =>
      libraryApi.updateItem(homeId!, itemId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => libraryApi.deleteItem(homeId!, itemId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// Tags
export function useTags(homeId: string | undefined) {
  return useQuery({
    queryKey: ['library', homeId, 'tags'],
    queryFn: () => libraryApi.listTags(homeId!),
    enabled: Boolean(homeId),
  });
}

// Attachments
export function useUploadAttachment(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, file }: { itemId: string; file: File }) =>
      libraryApi.uploadAttachment(homeId!, itemId, file),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteAttachment(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => libraryApi.deleteAttachment(homeId!, attachmentId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}
